import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";

const ChatContext = createContext(null);

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export const ChatProvider = ({ children }) => {
  const [messages, setMessages] = useState([]);
  const [connected, setConnected] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [displayName, setDisplayName] = useState(() => localStorage.getItem("gfn_name") || "");
  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);

  const connect = useCallback(() => {
    try {
      const wsUrl = BACKEND.replace(/^http/, "ws") + "/api/ws/chat";
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => setConnected(true);
      ws.onclose = () => {
        setConnected(false);
        // simple reconnect
        if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
        reconnectTimer.current = setTimeout(connect, 3000);
      };
      ws.onerror = () => {};
      ws.onmessage = (evt) => {
        try {
          const payload = JSON.parse(evt.data);
          if (payload.type === "history" && Array.isArray(payload.data)) {
            // Global drawer only cares about the global room (no room field)
            setMessages(payload.data.filter((m) => !m.room));
          } else if (payload.type === "chat" && payload.data) {
            if (payload.data.room) return; // room-scoped msg — not for global drawer
            setMessages((prev) => (prev.some((m) => m.id === payload.data.id) ? prev : [...prev, payload.data]));
          } else if (payload.type === "delete" && payload.data?.id) {
            setMessages((prev) => prev.filter((m) => m.id !== payload.data.id));
          } else if (payload.type === "clear") {
            setMessages([]);
          } else if (payload.type === "moderation" && payload.data?.reason) {
            if (typeof window !== "undefined" && window.__gfnOnModeration) {
              window.__gfnOnModeration(payload.data.reason);
            }
          }
        } catch (_) {}
      };
    } catch (e) {
      // ignore
    }
  }, []);

  useEffect(() => {
    connect();
    return () => {
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      if (wsRef.current) wsRef.current.close();
    };
  }, [connect]);

  const setName = useCallback((n) => {
    setDisplayName(n);
    localStorage.setItem("gfn_name", n);
  }, []);

  const sendMessage = useCallback((text) => {
    const message = (text || "").trim();
    if (!message) return;
    const name = (displayName || "Guest").trim();
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: "chat", name, message }));
    } else {
      fetch(`${BACKEND}/api/chat/messages`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, message }),
      })
        .then(async (r) => {
          if (r.status === 422) {
            const j = await r.json().catch(() => ({}));
            if (typeof window !== "undefined" && window.__gfnOnModeration) {
              window.__gfnOnModeration(j.detail || "Message blocked");
            }
          }
        })
        .catch(() => {});
    }
  }, [displayName]);

  const sendRequest = useCallback(async ({ song, station, note }) => {
    const name = (displayName || "Guest").trim();
    try {
      const res = await fetch(`${BACKEND}/api/requests`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, song, station, note }),
      });
      if (res.status === 422) {
        const j = await res.json().catch(() => ({}));
        return { ok: false, blocked: true, reason: j.detail || "Blocked by moderation" };
      }
      return { ok: res.ok };
    } catch {
      return { ok: false };
    }
  }, [displayName]);

  const value = {
    messages,
    connected,
    drawerOpen,
    setDrawerOpen,
    displayName,
    setName,
    sendMessage,
    sendRequest,
  };
  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};

export const useChat = () => {
  const ctx = useContext(ChatContext);
  if (!ctx) throw new Error("useChat must be inside ChatProvider");
  return ctx;
};
